import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { dirFor } from "@/lib/i18n/languages";
import {
  shouldSeedInventory,
  useEnsureProtocolInventory,
  useProtocol,
  useProtocolInventory,
  useProtocolPhotos,
  useSubmitProtocol,
} from "@/hooks/useProtocol";
import { formatMsg, type ProtocolCopy } from "@/lib/protocol-copy";
import { getProtocolCopy } from "@/lib/protocol-copy-all-languages";
import {
  getMissingCommonArea,
  MIN_COMMON_AREA_PHOTOS,
  MIN_ROOM_PHOTOS,
} from "@/lib/protocol";
import { ProtocolEntry } from "./ProtocolEntry";
import {
  isKeyPhotosStepComplete,
  KeyPhotosStep,
  MIN_KEY_PHOTOS,
} from "./steps/KeyPhotosStep";
import { PhotosStep, isPhotosStepComplete } from "./steps/PhotosStep";
import { InventoryStep, isInventoryComplete } from "./steps/InventoryStep";
import {
  AREA_KEYS,
  CommonAreasStep,
  isCommonAreasComplete,
} from "./steps/CommonAreasStep";
import { SignStep, type SignStepHandle } from "./steps/SignStep";
import { DoneStep } from "./steps/DoneStep";

type StepKey = "keys" | "photos" | "inventory" | "common" | "sign" | "done";

const STEP_ORDER: StepKey[] = ["keys", "photos", "inventory", "common", "sign"];

const STEP_LABEL_KEYS: Record<StepKey, keyof ProtocolCopy> = {
  keys: "stepKeys",
  photos: "stepPhotos",
  inventory: "stepInventory",
  common: "stepCommon",
  sign: "stepSign",
  done: "stepSign", // unused for indicator
};

/**
 * Inline orchestrator for the Übergabeprotokoll flow. Reads `?p=<uuid>` and
 * `&step=<name>` from the URL so the whole flow lives within the Dashboard
 * check-in/out tab while remaining shareable/refreshable.
 */
export function ProtocolFlow() {
  const { language } = useLanguage();
  const t = getProtocolCopy(language);
  const [searchParams, setSearchParams] = useSearchParams();
  const flowRef = useRef<HTMLDivElement>(null);

  const protocolId = searchParams.get("p");
  const stepParam = searchParams.get("step");
  const validStep: StepKey | null =
    stepParam === "keys" ||
    stepParam === "photos" ||
    stepParam === "inventory" ||
    stepParam === "common" ||
    stepParam === "sign" ||
    stepParam === "done"
      ? stepParam
      : null;

  useEffect(() => {
    if (!protocolId) return;

    const frame = window.requestAnimationFrame(() => {
      flowRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [protocolId, validStep]);

  // No protocol in URL → render entry
  if (!protocolId) {
    return (
      <div
        ref={flowRef}
        className="scroll-mt-28 md:scroll-mt-6"
        dir={dirFor(language)}
      >
        <ProtocolEntry
          onStart={(id) => {
            // Drop step so ResumeRouter computes the right one (works for both
            // brand-new drafts and resumes of in-progress ones).
            const next = new URLSearchParams(searchParams);
            next.set("tab", "checkin");
            next.set("p", id);
            next.delete("step");
            setSearchParams(next, { replace: true });
          }}
        />
      </div>
    );
  }

  if (!validStep) {
    return (
      <div
        ref={flowRef}
        className="scroll-mt-28 md:scroll-mt-6"
        dir={dirFor(language)}
      >
        <ResumeRouter protocolId={protocolId} t={t} />
      </div>
    );
  }

  return (
    <div
      ref={flowRef}
      className="scroll-mt-28 md:scroll-mt-6"
      dir={dirFor(language)}
    >
      <ActiveFlow protocolId={protocolId} step={validStep} t={t} />
    </div>
  );
}

/** Computes the right step to resume at based on what the user has already done. */
function ResumeRouter({
  protocolId,
  t,
}: {
  protocolId: string;
  t: ProtocolCopy;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: protocol, isLoading: pLoading } = useProtocol(protocolId);
  const { data: photos = [], isLoading: photosLoading } =
    useProtocolPhotos(protocolId);
  const { data: inventory = [], isLoading: invLoading } =
    useProtocolInventory(protocolId);

  useEffect(() => {
    if (pLoading || photosLoading || invLoading) return;
    if (!protocol) return;

    let target: StepKey;
    if (protocol.status === "submitted") target = "done";
    else {
      const keyCount = photos.filter((p) => p.category === "keys").length;
      const roomCount = photos.filter((p) => p.category === "room").length;
      if (!isKeyPhotosStepComplete(keyCount)) target = "keys";
      else if (!isPhotosStepComplete(roomCount)) target = "photos";
      else if (!isInventoryComplete(inventory)) target = "inventory";
      else if (!isCommonAreasComplete(photos)) target = "common";
      else target = "sign";
    }

    const sp = new URLSearchParams(searchParams);
    sp.set("tab", "checkin");
    sp.set("p", protocolId);
    sp.set("step", target);
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pLoading,
    photosLoading,
    invLoading,
    protocol?.id,
    protocol?.status,
    photos.length,
    inventory.length,
  ]);

  if (!pLoading && !protocol) {
    return (
      <div className="py-10 text-center text-sm text-neutral-500">
        {t.notFound}
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center py-20 text-neutral-400">
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>
  );
}

function ActiveFlow({
  protocolId,
  step,
  t,
}: {
  protocolId: string;
  step: StepKey;
  t: ProtocolCopy;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { language } = useLanguage();
  const { data: protocol, isLoading } = useProtocol(protocolId);
  const {
    data: photos = [],
    isLoading: photosLoading,
    isError: photosError,
  } = useProtocolPhotos(protocolId);
  const {
    data: inventory = [],
    isLoading: invLoading,
    isError: inventoryError,
  } = useProtocolInventory(protocolId);
  const ensureInventory = useEnsureProtocolInventory(protocolId);
  const submit = useSubmitProtocol(protocolId);
  const signRef = useRef<SignStepHandle | null>(null);
  const [signatureHasInk, setSignatureHasInk] = useState(false);
  const [submittedThisSession, setSubmittedThisSession] = useState(false);

  // Reset the signature gate whenever we (re-)enter the sign step for a
  // (possibly different) protocol so a stale "has ink" flag can't leak in.
  useEffect(() => {
    setSignatureHasInk(false);
    setSubmittedThisSession(false);
  }, [protocolId, protocol?.kind]);

  // Give a draft its checklist if it has none. The reminder tick files a
  // protocol row with no inventory when a window closes unsubmitted; once an
  // admin reopens that protocol the tenant resumes THAT row, and an empty
  // checklist is a dead end — nothing to fill in and `isInventoryComplete([])`
  // never unlocks Continue. Seeds once: the row count changes, so the effect
  // no-ops on its next run, and a failure stops it retrying in a loop.
  useEffect(() => {
    if (
      !shouldSeedInventory({
        status: protocol?.status,
        inventoryCount: inventory.length,
        isLoading: invLoading,
        alreadyFailed: ensureInventory.isError,
      }) ||
      ensureInventory.isPending
    ) {
      return;
    }
    ensureInventory.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutation identity changes every render; the guard above is the real gate
  }, [
    protocol?.status,
    inventory.length,
    invLoading,
    ensureInventory.isError,
    ensureInventory.isPending,
  ]);

  const keyPhotosCount = photos.filter((p) => p.category === "keys").length;
  const roomPhotosCount = photos.filter((p) => p.category === "room").length;
  const firstIncompleteStep: StepKey | null = !isKeyPhotosStepComplete(
    keyPhotosCount
  )
    ? "keys"
    : !isPhotosStepComplete(roomPhotosCount)
      ? "photos"
      : !isInventoryComplete(inventory)
        ? "inventory"
        : !isCommonAreasComplete(photos)
          ? "common"
          : null;

  // A copied/deep-linked URL must not bypass an earlier required step. This
  // also sends an already-submitted protocol straight to its completion view.
  useEffect(() => {
    if (
      isLoading ||
      photosLoading ||
      invLoading ||
      photosError ||
      inventoryError ||
      !protocol
    )
      return;

    const target =
      protocol.status === "submitted"
        ? "done"
        : firstIncompleteStep &&
            STEP_ORDER.indexOf(step) > STEP_ORDER.indexOf(firstIncompleteStep)
          ? firstIncompleteStep
          : step === "done" && !submittedThisSession
            ? "sign"
            : null;
    if (!target || target === step) return;

    const sp = new URLSearchParams(searchParams);
    sp.set("tab", "checkin");
    sp.set("p", protocolId);
    sp.set("step", target);
    setSearchParams(sp, { replace: true });
  }, [
    firstIncompleteStep,
    invLoading,
    inventoryError,
    isLoading,
    photosError,
    photosLoading,
    protocol,
    protocolId,
    searchParams,
    setSearchParams,
    step,
    submittedThisSession,
  ]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-neutral-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (!protocol) {
    return (
      <div className="py-10 text-center text-sm text-neutral-500">
        {t.notFound}
      </div>
    );
  }

  const goStep = (next: StepKey) => {
    const sp = new URLSearchParams(searchParams);
    sp.set("tab", "checkin");
    sp.set("p", protocolId);
    sp.set("step", next);
    setSearchParams(sp, { replace: false });
  };

  const exit = () => {
    // Drop protocol + step params, keep tab=checkin.
    const sp = new URLSearchParams();
    sp.set("tab", "checkin");
    setSearchParams(sp, { replace: true });
  };

  const stepIndex = STEP_ORDER.indexOf(step);

  // Validate the current step's completion before continuing.
  const canContinue = (() => {
    if (step === "keys") return isKeyPhotosStepComplete(keyPhotosCount);
    if (step === "photos") return isPhotosStepComplete(roomPhotosCount);
    if (step === "inventory") return isInventoryComplete(inventory);
    if (step === "common") return isCommonAreasComplete(photos);
    if (step === "sign")
      return (
        (photosError || inventoryError || !firstIncompleteStep) &&
        signatureHasInk
      );
    return false;
  })();

  // COL-433: explain why Continue is disabled instead of leaving tenants to
  // guess which of the three common-areas tabs is still short on photos.
  const continueHint = (() => {
    // Don't flash a false "missing photos" hint off `photos`'s empty-array
    // default while the query is still loading.
    if (canContinue || photosLoading) return null;
    if (step === "keys") {
      const missing = MIN_KEY_PHOTOS - keyPhotosCount;
      return missing > 0 ? formatMsg(t.continueHintKeys, { n: missing }) : null;
    }
    if (step === "photos") {
      const missing = MIN_ROOM_PHOTOS - roomPhotosCount;
      return missing > 0
        ? formatMsg(t.continueHintPhotos, { n: missing })
        : null;
    }
    if (step === "common") {
      const area = getMissingCommonArea(photos);
      if (!area) return null;
      const count = photos.filter((p) => p.category === area).length;
      return formatMsg(t.continueHintCommon, {
        n: MIN_COMMON_AREA_PHOTOS - count,
        area: t[AREA_KEYS[area]] as string,
      });
    }
    return null;
  })();

  const handleContinue = async () => {
    if (step === "sign") {
      if (photosError || inventoryError) {
        toast.error(t.loadProtocolFailed);
        return;
      }
      if (firstIncompleteStep) {
        const missingMessage =
          firstIncompleteStep === "keys"
            ? formatMsg(t.continueHintKeys, {
                n: Math.max(1, MIN_KEY_PHOTOS - keyPhotosCount),
              })
            : firstIncompleteStep === "photos"
              ? formatMsg(t.continueHintPhotos, {
                  n: Math.max(1, MIN_ROOM_PHOTOS - roomPhotosCount),
                })
              : firstIncompleteStep === "inventory"
                ? t.inventorySubtitle
                : (() => {
                    const area = getMissingCommonArea(photos);
                    if (!area) return t.commonSubtitle;
                    const count = photos.filter(
                      (photo) => photo.category === area
                    ).length;
                    return formatMsg(t.continueHintCommon, {
                      n: MIN_COMMON_AREA_PHOTOS - count,
                      area: t[AREA_KEYS[area]] as string,
                    });
                  })();
        toast.error(missingMessage);
        goStep(firstIncompleteStep);
        return;
      }
      if (!signRef.current || signRef.current.isEmpty()) {
        toast.error(t.pleaseSign);
        return;
      }
      try {
        await submit.mutateAsync({
          signatureDataUrl: signRef.current.toDataURL(),
          language: language === "de" ? "de" : "en",
        });
        // Keep Done stable while React Query refetches the protocol. The
        // mutation resolves before its invalidation finishes, so the cached
        // status can still be `draft` for one render.
        setSubmittedThisSession(true);
        goStep("done");
      } catch (err: any) {
        toast.error(err?.message ?? t.saveFailed);
      }
      return;
    }
    const nextIdx = stepIndex + 1;
    if (nextIdx < STEP_ORDER.length) goStep(STEP_ORDER[nextIdx]);
  };

  const handleBack = () => {
    const prevIdx = stepIndex - 1;
    if (prevIdx >= 0) goStep(STEP_ORDER[prevIdx]);
    else exit();
  };

  if (step === "done") {
    return <DoneStep protocolId={protocolId} t={t} onClose={exit} />;
  }

  const kindLabel = protocol.kind === "move_in" ? t.moveIn : t.moveOut;

  const progressPct = Math.round(((stepIndex + 1) / STEP_ORDER.length) * 100);

  return (
    <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
      {/* Sticky step header — mobile shows compact title + thin progress bar;
          desktop shows full pill stepper. */}
      <div className="sticky top-0 z-10 border-b border-neutral-100 bg-white/95 px-5 py-3 backdrop-blur md:relative md:px-8 md:py-5">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
              {kindLabel} · {t.step} {stepIndex + 1} {t.of} {STEP_ORDER.length}
            </div>
            <div className="truncate text-base font-semibold text-neutral-900 md:text-lg">
              {t[STEP_LABEL_KEYS[step]] as string}
            </div>
          </div>
          <button
            type="button"
            onClick={exit}
            className="shrink-0 rounded-full border border-neutral-200 px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            {t.pause}
          </button>
        </div>

        {/* Mobile: thin progress bar */}
        <div className="mt-3 h-1 w-full rounded-full bg-neutral-100 md:hidden">
          <div
            className="h-1 rounded-full bg-emerald-500 transition-all"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {/* Desktop: full pill stepper */}
        <ol className="mt-4 hidden items-center gap-3 md:flex">
          {STEP_ORDER.map((s, idx) => {
            const isActive = s === step;
            const isPast = idx < stepIndex;
            const reachable = idx <= stepIndex;
            return (
              <li key={s} className="flex shrink-0 items-center gap-3">
                <button
                  type="button"
                  disabled={!reachable}
                  onClick={() => reachable && goStep(s)}
                  className={[
                    "flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : isPast
                        ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                        : "bg-neutral-100 text-neutral-500",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold",
                      isActive
                        ? "bg-white/20 text-white"
                        : isPast
                          ? "bg-emerald-500 text-white"
                          : "bg-white text-neutral-400",
                    ].join(" ")}
                  >
                    {isPast ? <CheckCircle2 className="h-3 w-3" /> : idx + 1}
                  </span>
                  {t[STEP_LABEL_KEYS[s]] as string}
                </button>
                {idx < STEP_ORDER.length - 1 ? (
                  <span className="h-px w-6 bg-neutral-200" />
                ) : null}
              </li>
            );
          })}
        </ol>
      </div>

      {/* Step body — extra bottom padding on mobile so content isn't hidden
          behind the sticky toolbar. */}
      <div className="px-5 pb-28 pt-6 md:px-8 md:pb-8 md:pt-8">
        {step === "keys" && (
          <KeyPhotosStep protocolId={protocolId} kind={protocol.kind} />
        )}
        {step === "photos" && <PhotosStep protocolId={protocolId} t={t} />}
        {step === "inventory" && (
          <InventoryStep protocolId={protocolId} t={t} />
        )}
        {step === "common" && <CommonAreasStep protocolId={protocolId} t={t} />}
        {step === "sign" && (
          <SignStep
            ref={signRef}
            protocolId={protocolId}
            t={t}
            onInkChange={setSignatureHasInk}
          />
        )}
      </div>

      {/* Bottom toolbar — sticky to viewport on mobile (so CTA is always in
          thumb reach), inline at the bottom of the card on desktop. */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-neutral-100 bg-white/95 px-4 pb-[max(env(safe-area-inset-bottom),12px)] pt-3 backdrop-blur md:relative md:inset-auto md:border-neutral-100 md:bg-neutral-50/50 md:px-8 md:py-5 md:backdrop-blur-0">
        {continueHint ? (
          <p className="mx-auto mb-2 w-full max-w-5xl text-center text-sm text-amber-600 md:text-left">
            {continueHint}
          </p>
        ) : null}
        <div className="mx-auto flex w-full max-w-5xl items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            aria-label={t.back}
            className="inline-flex h-12 shrink-0 items-center justify-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 text-sm font-medium text-neutral-700 hover:bg-neutral-50 md:h-11"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">{t.back}</span>
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={!canContinue || submit.isPending}
            className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-md shadow-primary/30 transition hover:bg-primary/90 active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-neutral-300 disabled:text-white disabled:shadow-none md:h-11 md:max-w-[260px] md:flex-none md:ml-auto"
          >
            {submit.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowRight className="h-4 w-4" />
            )}
            {step === "sign" ? t.finalizeCta : t.continue}
          </button>
        </div>
      </div>
    </div>
  );
}
